export const TABLE_MIN = 1
export const TABLE_MAX_ROWS = 20
export const TABLE_MAX_COLS = 20

export const clampTableDimension = (value: unknown, max: number, fallback = 3): number => {
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return fallback
  return Math.min(max, Math.max(TABLE_MIN, Math.floor(parsed)))
}

export const defaultColumnHeaders = (count: number): string[] =>
  Array.from({ length: count }, (_, index) => `Column ${index + 1}`)

export const defaultRowHeaders = (count: number): string[] =>
  Array.from({ length: count }, (_, index) => `Row ${index + 1}`)

export const resizeHeaders = (
  headers: string[] | undefined,
  count: number,
  buildDefault: (n: number) => string[]
): string[] => {
  const next = [...(headers || [])]
  const defaults = buildDefault(count)
  while (next.length < count) {
    next.push(defaults[next.length] || `Item ${next.length + 1}`)
  }
  return next.slice(0, count)
}

const parseTableMatrix = (value: unknown): string[][] | null => {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    if (!Array.isArray(value[0])) {
      return [value.map((cell) => String(cell ?? ''))]
    }
    return value.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []))
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value)
      if (!Array.isArray(parsed) || parsed.length === 0) return null
      if (!Array.isArray(parsed[0])) {
        return [parsed.map((cell) => String(cell ?? ''))]
      }
      return parsed.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []))
    } catch {
      return null
    }
  }
  return null
}

export const parseTableCells = (value: unknown, rows: number, cols: number): string[][] => {
  const matrix = parseTableMatrix(value)
  if (!matrix) {
    return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''))
  }
  return Array.from({ length: rows }, (_, rowIndex) =>
    Array.from({ length: cols }, (_, colIndex) => String(matrix[rowIndex]?.[colIndex] ?? ''))
  )
}

export const stringifyTableCells = (cells: string[][]): string => JSON.stringify(cells)

export const isTableValueEmpty = (value: unknown): boolean => {
  if (typeof value !== 'string' || !value.trim()) return true
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed) || parsed.length === 0) return true
    return parsed.some(
      (row) => !Array.isArray(row) || row.some((cell) => String(cell ?? '').trim().length === 0)
    )
  } catch {
    return true
  }
}

export const getTableDimensions = (element: {
  tableRowCount?: number
  tableColumnCount?: number
}) => ({
  rows: clampTableDimension(element.tableRowCount, TABLE_MAX_ROWS),
  cols: clampTableDimension(element.tableColumnCount, TABLE_MAX_COLS)
})

export type TableColumnType = 'text' | 'number' | 'file'

export const TABLE_COLUMN_TYPE_OPTIONS: Array<{ value: TableColumnType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'file', label: 'File upload' }
]

export const resizeColumnTypes = (
  columnTypes: string[] | undefined,
  count: number
): TableColumnType[] => {
  const next = [...(columnTypes || [])].map((type) =>
    type === 'number' || type === 'file' ? type : 'text'
  )
  while (next.length < count) {
    next.push('text')
  }
  return next.slice(0, count) as TableColumnType[]
}

export const getColumnTypes = (element: { columnTypes?: string[]; tableColumnCount?: number }) => {
  const { cols } = getTableDimensions(element)
  return resizeColumnTypes(element.columnTypes, cols)
}

export const buildTableRowEditFieldId = (tableFieldId: number, rowIndex: number): number =>
  tableFieldId * 1000 + rowIndex

export const parseTableRowEditFieldId = (
  fieldId: number
): { tableFieldId: number; rowIndex: number } | null => {
  if (fieldId <= 0) return null
  const tableFieldId = Math.floor(fieldId / 1000)
  const rowIndex = fieldId % 1000
  if (tableFieldId <= 0 || rowIndex < 0) return null
  return { tableFieldId, rowIndex }
}

export const isTableRowEditFieldId = (fieldId: number): boolean =>
  fieldId >= 1000 && fieldId % 1000 < 100

/** Parent-scoped edit field IDs: parent 9 → 901, 902, … (never collide with top-level fieldIds). */
export const buildParentScopedEditFieldId = (parentFieldId: number, index: number): number =>
  Number(`${parentFieldId}${String(index + 1).padStart(2, '0')}`)

export const isParentScopedEditFieldId = (editFieldId: number, parentFieldId: number): boolean => {
  const parent = Number(parentFieldId)
  const id = Number(editFieldId)
  if (!Number.isFinite(parent) || parent <= 0 || !Number.isFinite(id) || id <= 0) {
    return false
  }
  const parentStr = String(parent)
  const idStr = String(id)
  if (!idStr.startsWith(parentStr) || idStr.length <= parentStr.length) {
    return false
  }
  const suffix = idStr.slice(parentStr.length)
  return /^\d{2,}$/.test(suffix) && Number(suffix) > 0
}

/**
 * Resolve a stable edit-field id.
 * When parentFieldId is provided (non-table), always return a parent-scoped id so
 * conditional edits never share ids with top-level form fields (e.g. Plain input).
 * Table context keeps relative ids (1, 2, 3…) which are later synthesized.
 */
export const resolvePrefillEditFieldId = (
  editField: { fieldId?: number | null },
  index: number,
  parentFieldId?: number
): number => {
  const parent = Number(parentFieldId)
  const expectedScoped =
    Number.isFinite(parent) && parent > 0 ? buildParentScopedEditFieldId(parent, index) : index + 1

  const parsed = Number(editField?.fieldId)
  if (Number.isFinite(parsed) && parsed > 0) {
    if (Number.isFinite(parent) && parent > 0) {
      if (isParentScopedEditFieldId(parsed, parent)) return parsed
      return expectedScoped
    }
    return parsed
  }

  return expectedScoped
}

/** Reassign duplicate top-level fieldIds across pages (first occurrence keeps its id). */
export const ensureUniqueFormFieldIds = (pages: any[][]): any[][] => {
  if (!Array.isArray(pages)) return pages

  let maxId = 0
  pages.forEach((page) => {
    ;(page || []).forEach((element: any) => {
      const id = Number(element?.fieldId ?? element?.id)
      if (Number.isFinite(id) && id > maxId) maxId = id
    })
  })

  const seen = new Set<number>()
  return pages.map((page) =>
    (page || []).map((element: any) => {
      const id = Number(element?.fieldId ?? element?.id)
      if (!Number.isFinite(id) || id <= 0) {
        maxId += 1
        seen.add(maxId)
        return { ...element, fieldId: maxId }
      }
      if (seen.has(id)) {
        maxId += 1
        seen.add(maxId)
        return { ...element, fieldId: maxId }
      }
      seen.add(id)
      return element
    })
  )
}

/** Normalize edit-field ids and guarantee uniqueness within a sibling list. */
export const normalizePrefillEditFieldIds = (
  editFields: Array<{ fieldId?: number | null; [key: string]: any }> | undefined | null,
  parentFieldId?: number
): any[] => {
  if (!Array.isArray(editFields)) return []
  const used = new Set<number>()
  return editFields.map((editField, index) => {
    let fieldId = resolvePrefillEditFieldId(editField, index, parentFieldId)
    if (used.has(fieldId)) {
      let probe = index + 1
      do {
        fieldId =
          Number.isFinite(Number(parentFieldId)) && Number(parentFieldId) > 0
            ? buildParentScopedEditFieldId(Number(parentFieldId), probe)
            : probe + 1
        probe += 1
      } while (used.has(fieldId))
    }
    used.add(fieldId)
    return { ...editField, fieldId }
  })
}

export const buildTableRowConditionalEditFieldId = (
  tableFieldId: number,
  rowIndex: number,
  editFieldId: number
): number => tableFieldId * 100000 + rowIndex * 1000 + editFieldId

export const buildTableColumnConditionalEditFieldId = (
  tableFieldId: number,
  rowIndex: number,
  colIndex: number,
  editFieldId: number
): number => tableFieldId * 10000000 + rowIndex * 100000 + colIndex * 1000 + editFieldId
